import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Button, Space } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, FullscreenOutlined } from '@ant-design/icons';

const TimelineRenderer = ({ data, config = {} }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 400 });

  useEffect(() => {
    if (containerRef.current) {
      const width = config.width || containerRef.current.offsetWidth || 800;
      const height = config.height || 400;
      setDimensions({ width, height });
    }
  }, [config]);

  useEffect(() => {
    if (!data || !dimensions.width) return;
    try {
      renderTimeline();
    } catch (error) {
      console.error('Timeline rendering error:', error);
    }
  }, [data, dimensions]);

  const renderTimeline = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    // Clear previous
    d3.select(svgElement).selectAll('*').remove();

    const width = dimensions.width;
    const height = dimensions.height;
    const margin = { top: 60, right: 60, bottom: 60, left: 60 };

    const svg = d3.select(svgElement)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    // Add gradient definitions
    const defs = svg.append('defs');

    // Gradient for timeline
    const timelineGradient = defs.append('linearGradient')
      .attr('id', 'timelineGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%');
    timelineGradient.append('stop').attr('offset', '0%').attr('stop-color', '#667eea');
    timelineGradient.append('stop').attr('offset', '100%').attr('stop-color', '#764ba2');

    // Gradient for milestones
    const milestoneGradient = defs.append('linearGradient')
      .attr('id', 'milestoneGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    milestoneGradient.append('stop').attr('offset', '0%').attr('stop-color', '#4299e1');
    milestoneGradient.append('stop').attr('offset', '100%').attr('stop-color', '#3182ce');

    // Shadow filter
    const filter = defs.append('filter')
      .attr('id', 'timelineShadow')
      .attr('x', '-50%').attr('y', '-50%')
      .attr('width', '200%').attr('height', '200%');
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 3);
    filter.append('feOffset').attr('dx', 0).attr('dy', 2).attr('result', 'offsetblur');
    filter.append('feComponentTransfer').append('feFuncA').attr('type', 'linear').attr('slope', 0.3);
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Add title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 30)
      .attr('text-anchor', 'middle')
      .attr('font-size', '18px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(data.title || 'Project Timeline')
      .attr('opacity', 0)
      .transition()
      .duration(800)
      .attr('opacity', 1);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 2])
      .on('zoom', (event) => {
        g.attr('transform', `translate(${margin.left},${margin.top}) ${event.transform}`);
      });

    svg.call(zoom);

    // Process timeline data
    const timelineData = data.milestones || data.items || [];
    const timelineWidth = width - margin.left - margin.right;
    const timelineHeight = height - margin.top - margin.bottom;
    
    // Calculate positions
    const xScale = d3.scaleLinear()
      .domain([0, timelineData.length - 1])
      .range([0, timelineWidth]);

    const centerY = timelineHeight / 2;

    // Draw main timeline line (background)
    g.append('line')
      .attr('x1', 0)
      .attr('y1', centerY)
      .attr('x2', timelineWidth)
      .attr('y2', centerY)
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 8)
      .attr('opacity', 0)
      .transition()
      .duration(800)
      .attr('opacity', 1);

    // Draw main timeline line (foreground)
    g.append('line')
      .attr('x1', 0)
      .attr('y1', centerY)
      .attr('x2', timelineWidth)
      .attr('y2', centerY)
      .attr('stroke', 'url(#timelineGradient)')
      .attr('stroke-width', 4)
      .attr('filter', 'url(#timelineShadow)')
      .attr('opacity', 0)
      .transition()
      .duration(1000)
      .delay(200)
      .attr('opacity', 1);

    // Add progress segments between milestones
    for (let i = 0; i < timelineData.length - 1; i++) {
      const x1 = xScale(i);
      const x2 = xScale(i + 1);
      
      g.append('line')
        .attr('x1', x1 + 16) // Start after milestone circle
        .attr('y1', centerY)
        .attr('x2', x2 - 16) // End before next milestone circle
        .attr('y2', centerY)
        .attr('stroke', '#4299e1')
        .attr('stroke-width', 6)
        .attr('stroke-dasharray', '8,4')
        .attr('opacity', 0)
        .transition()
        .duration(600)
        .delay(1000 + (i * 200))
        .attr('opacity', 0.8);
    }

    // Add timeline arrows at both ends
    g.append('polygon')
      .attr('points', `${timelineWidth-10},${centerY-5} ${timelineWidth},${centerY} ${timelineWidth-10},${centerY+5}`)
      .attr('fill', '#667eea')
      .attr('opacity', 0)
      .transition()
      .duration(500)
      .delay(1200)
      .attr('opacity', 1);

    g.append('circle')
      .attr('cx', 0)
      .attr('cy', centerY)
      .attr('r', 4)
      .attr('fill', '#667eea')
      .attr('opacity', 0)
      .transition()
      .duration(500)
      .delay(1200)
      .attr('opacity', 1);

    // Draw milestones
    const milestones = g.selectAll('.milestone')
      .data(timelineData)
      .enter()
      .append('g')
      .attr('class', 'milestone')
      .attr('transform', (d, i) => `translate(${xScale(i)}, ${centerY})`)
      .style('opacity', 0)
      .style('cursor', 'pointer');

    // Milestone circles (outer ring)
    milestones.append('circle')
      .attr('r', 16)
      .attr('fill', 'white')
      .attr('stroke', '#667eea')
      .attr('stroke-width', 3)
      .attr('filter', 'url(#timelineShadow)');

    // Milestone circles (inner circle)
    milestones.append('circle')
      .attr('r', 10)
      .attr('fill', 'url(#milestoneGradient)')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Milestone labels
    milestones.each(function(d, i) {
      const milestone = d3.select(this);
      const isEven = i % 2 === 0;
      const yOffset = isEven ? -60 : 60;
      const textAnchor = 'middle';

      // Background for text
      const textBg = milestone.append('rect')
        .attr('x', -80)
        .attr('y', yOffset - 25)
        .attr('width', 160)
        .attr('height', 50)
        .attr('rx', 8)
        .attr('fill', 'white')
        .attr('stroke', '#e0e0e0')
        .attr('stroke-width', 1)
        .attr('filter', 'url(#timelineShadow)');

      // Title
      milestone.append('text')
        .attr('text-anchor', textAnchor)
        .attr('y', yOffset - 8)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', '#333')
        .text(d.title || d.name || `Milestone ${i + 1}`);

      // Timeline/Duration
      if (d.timeline || d.duration || d.month) {
        milestone.append('text')
          .attr('text-anchor', textAnchor)
          .attr('y', yOffset + 8)
          .attr('font-size', '10px')
          .attr('fill', '#666')
          .text(d.timeline || d.duration || d.month);
      }

      // Connection line
      milestone.append('line')
        .attr('x1', 0)
        .attr('y1', isEven ? -16 : 16)
        .attr('x2', 0)
        .attr('y2', yOffset + (isEven ? 25 : -25))
        .attr('stroke', '#4299e1')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '4,2')
        .attr('opacity', 0.7);
    });

    // Animate milestones
    milestones.transition()
      .duration(800)
      .delay((d, i) => i * 200)
      .style('opacity', 1);

    // Store original positions for each milestone
    milestones.each(function(d, i) {
      d3.select(this).attr('data-original-x', xScale(i));
    });

    // Add hover effects using only scale, preserving position
    milestones.on('mouseenter', function(event, d) {
      const originalX = d3.select(this).attr('data-original-x');
      
      d3.select(this)
        .transition()
        .duration(200)
        .attr('transform', `translate(${originalX}, ${centerY}) scale(1.1)`);
    });

    milestones.on('mouseleave', function(event, d) {
      const originalX = d3.select(this).attr('data-original-x');
      
      d3.select(this)
        .transition()
        .duration(200)
        .attr('transform', `translate(${originalX}, ${centerY}) scale(1)`);
    });
  };

  const handleZoomIn = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 1.3);
  };

  const handleZoomOut = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 0.7);
  };

  const handleReset = () => {
    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(750)
      .call(
        d3.zoom().transform,
        d3.zoomIdentity
      );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <Space style={{ marginBottom: '12px' }}>
          <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small">
            Zoom In
          </Button>
          <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small">
            Zoom Out
          </Button>
          <Button icon={<FullscreenOutlined />} onClick={handleReset} size="small">
            Reset
          </Button>
        </Space>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg ref={svgRef} style={{ border: '1px solid #f0f0f0', borderRadius: '4px', maxWidth: '100%', height: 'auto' }} />
        </div>
      </div>
    </div>
  );
};

export default TimelineRenderer;